import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { User, UserPlus, Eye, EyeOff, Heart, BookOpen } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  sendEmailVerification, 
  onAuthStateChanged,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo
} from 'firebase/auth';
import logo from '../assets/brandLogo.png';
import login_hero_image from '../assets/eko-_transparent-removebg-preview.png';
import sectionBackground from '../assets/landing/backgroundupdated.png';
// import ekoIllustration from '../assets/eko.png';
import { saveUserData } from '../services/api';
import { trackEvent } from '../services/metaPixel';
interface User {
  id: string;
  email: string;
  name: string;
  // role: 'parent' | 'teacher'; // Removed role field
  createdAt: string;
}

interface AuthenticationProps {
  onAuthSuccess: (user: User) => void;
}

export const Authentication: React.FC<AuthenticationProps> = ({ onAuthSuccess }) => {
  const [activeTab, setActiveTab] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '' // Removed role field
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const auth = getAuth();
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('');
  const [generalError, setGeneralError] = useState<string>("");
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState('');
  const [isResetLoading, setIsResetLoading] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Reload user to get latest email verification status
        await user.reload();
        
        if (user.emailVerified) {
          setShowVerificationModal(false);
          onAuthSuccess({
            id: user.uid,
            email: user.email || '',
            name: user.displayName || formData.name,
            createdAt: new Date().toISOString(),
          });
        } else if (showVerificationModal) {
          // User exists but email not verified, keep showing modal
          setVerificationStatus('Please verify your email to continue.');
        }
      }
    });

    return () => unsubscribe();
  }, [showVerificationModal]);

  const checkVerificationStatus = () => {
    const interval = setInterval(async () => {
      try {
        await auth.currentUser?.reload();
        const currentUser = auth.currentUser;
        
        if (currentUser?.emailVerified) {
          clearInterval(interval);
          setVerificationStatus('Email verified successfully!');
          setTimeout(async () => {
            setShowVerificationModal(false);
            if (currentUser) {
              onAuthSuccess({
                id: currentUser.uid,
                email: currentUser.email || '',
                name: currentUser.displayName || formData.name,
                createdAt: new Date().toISOString(),
              });
            }
          }, 1500);
        }
      } catch (error) {
        console.error('Error checking verification status:', error);
      }
    }, 3000);

    // Clear interval after 5 minutes
    const timeoutId = setTimeout(() => {
      clearInterval(interval);
      setVerificationStatus('Verification timeout. Please try logging in again.');
      setTimeout(() => setShowVerificationModal(false), 1500);
    }, 300000);

    // Cleanup function to clear both timers
    return () => {
      clearInterval(interval);
      clearTimeout(timeoutId);
    };
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Clear form data when switching tabs
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setFormData({
      email: '',
      password: '',
      name: ''
    });
    setErrors({});
    setGeneralError('');
  };

  // Check if login form is valid for button state
  const isLoginFormValid = formData.email.trim() && formData.password.trim();
  
  // Check if signup form is valid for button state
  const isSignupFormValid = formData.email.trim() && formData.password.trim() && formData.name.trim();

  const validateForm = (isSignup: boolean) => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (isSignup && !formData.name) {
      newErrors.name = 'Name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  function getFriendlyErrorMessage(error: any): string {
    switch (error.code) {
      case 'auth/invalid-login-credentials':
      case 'auth/invalid-credential':
        return 'Invalid email or password. Please try again.';
      case 'auth/user-not-found':
        return 'Account not found. Please sign up first.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/email-already-in-use':
        return 'An account with this email already exists.';
      case 'auth/weak-password':
        return 'Password is too weak. Please use at least 6 characters.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection and try again.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please wait a moment and try again.';
      case 'auth/invalid-email':
        return 'Invalid email address format.';
      case 'auth/popup-closed-by-user':
        return 'Google sign-in was cancelled. Please try again.';
      case 'auth/cancelled-popup-request':
        return 'Google sign-in was cancelled. Please try again.';
      case 'auth/popup-blocked':
        return 'The Google pop-up was blocked. Please allow pop-ups for this site and try again.';
      case 'auth/account-exists-with-different-credential':
        return 'An account already exists with this email using a different sign-in method. Please log in that way instead.';
      case 'auth/operation-not-allowed':
        return 'This operation is not allowed. Please contact support.';
      default:
        return error.message || 'An unexpected error occurred.';
    }
  }

  const handleLogin = async () => {
    setGeneralError("");
    if (!validateForm(false)) return;
    setIsLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      if (!userCredential.user.emailVerified) {
        await sendEmailVerification(userCredential.user);
        setShowVerificationModal(true);
        setVerificationStatus('Email not verified. Verification email sent! Please check your inbox.');
        checkVerificationStatus();
      } else {
        onAuthSuccess({
          id: userCredential.user.uid,
          email: userCredential.user.email || '',
          name: userCredential.user.displayName || formData.name,
          createdAt: new Date().toISOString(),
        });
      }
    } catch (error: any) {
      setGeneralError(getFriendlyErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async () => {
    setGeneralError("");
    if (!validateForm(true)) return;
    setIsLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      await saveUserData({
        email: userCredential.user.email,
        name: formData.name,
        password: formData.password
      });
      await sendEmailVerification(userCredential.user);
      trackEvent('CompleteRegistration', { method: 'email' }, { eventID: `signup-${userCredential.user.uid}` });
      setShowVerificationModal(true);
      setVerificationStatus('Verification email sent! Please check your inbox.');
      checkVerificationStatus();
    } catch (error: any) {
      setGeneralError(getFriendlyErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGeneralError("");
    setIsLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      // Seed the user record on first login. saveUserData is idempotent on
      // the backend, so a returning Google user keeps their children and
      // payment status instead of being overwritten.
      try {
        await saveUserData({
          email: user.email || '',
          name: user.displayName || '',
        });
      } catch (saveErr) {
        // The auth itself succeeded; a save failure must not block login.
        console.error('Error saving Google user data:', saveErr);
      }
      // This button is both sign-up and sign-in for Google users, so only the
      // first visit is a registration — otherwise every returning login would
      // inflate the conversion count the ads are optimised against.
      if (getAdditionalUserInfo(result)?.isNewUser) {
        trackEvent('CompleteRegistration', { method: 'google' }, { eventID: `signup-${user.uid}` });
      }
      onAuthSuccess({
        id: user.uid,
        email: user.email || '',
        name: user.displayName || '',
        createdAt: new Date().toISOString(),
      });
    } catch (error: any) {
      setGeneralError(getFriendlyErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!resetEmail.trim()) {
      setResetStatus('Please enter your email address');
      return;
    }

    if (!validateEmail(resetEmail)) {
      setResetStatus('Please enter a valid email address');
      return;
    }

    setIsResetLoading(true);
    setResetStatus('');

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetStatus('Password reset email sent! Please check your inbox and follow the instructions to reset your password.');
    } catch (error: any) {
      switch (error.code) {
        case 'auth/user-not-found':
          setResetStatus('No account found with this email address. Please check your email or sign up.');
          break;
        case 'auth/invalid-email':
          setResetStatus('Invalid email address format.');
          break;
        case 'auth/too-many-requests':
          setResetStatus('Too many reset attempts. Please wait a moment and try again.');
          break;
        default:
          setResetStatus('An error occurred. Please try again later.');
      }
    } finally {
      setIsResetLoading(false);
    }
  };

  const openResetModal = () => {
    setShowResetModal(true);
    setResetEmail(formData.email); // Pre-fill with current email if available
    setResetStatus('');
  };

  const closeResetModal = () => {
    setShowResetModal(false);
    setResetEmail('');
    setResetStatus('');
  };

  return (
    <div 
      className="min-h-screen w-full flex flex-col md:flex-row font-[Nunito_Sans,sans-serif] relative"
      style={{
        backgroundColor: '#F3EAD7',
        backgroundImage: `url(${sectionBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center bottom',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      <link href="https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      {/* Image Section */}
      <div className="hidden md:flex flex-1 flex-col items-center justify-center bg-gradient-to-br from-[#F7E1E8]/15 to-[#FBE5DA]/15">
        <img 
          src={logo}
          alt="DPP Logo"
          className="w-[18rem] h-[18rem] mt-[1px] object-contain drop-shadow-xl rounded-2xl mb-4"
        />
        {/* Temporarily commented out Eko illustration */}
        {/* <img 
          src={ekoIllustration}
          alt="Eko Mascot"
          className="w-20 h-20 mb-4 object-contain drop-shadow-xl rounded-full"
        /> */}
        <img 
          src={login_hero_image}
          alt="DPP Illustration"
          className="w-1/2 max-w-sm object-contain drop-shadow-xl rounded-3xl"
        />
      </div>
      {/* Auth Section */}
  <div className="flex-1 flex justify-center p-3 min-[992px]:items-center">
        <div className="w-full max-w-sm">
          {/* Mobile Image */}
          <div className="md:hidden text-center mb-2">
            <img 
              src={logo}
              alt="DPP Logo"
              className="w-[9rem] h-[9rem] mx-auto object-contain mb-[2.5rem] mt-[30px]"
            />
          </div>
          {/* Spacer to keep modal position stable after heading removal (desktop only) */}
          <div className="hidden md:block mb-2" style={{ height: '2.75rem' }} aria-hidden="true" />
          <Card className="p-6 bg-white/95 backdrop-blur-sm border border-[#E4D6BC]/40 rounded-3xl shadow-lg">
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-5 bg-[#FFF2E6] p-1 rounded-2xl shadow-sm">
                <TabsTrigger 
                  value="login" 
                  className="rounded-xl data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#F7B26A] data-[state=active]:to-[#ECA7C1] data-[state=active]:text-white data-[state=active]:shadow-md text-[#B58A5E] font-bold text-base py-2.5 transition-all"
                  style={{ fontFamily: '"Nunito Sans", system-ui, sans-serif' }}
                >
                  <User className="w-4 h-4 mr-2" />
                  Login
                </TabsTrigger>
                <TabsTrigger 
                  value="signup"
                  className="rounded-xl data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#F7B26A] data-[state=active]:to-[#ECA7C1] data-[state=active]:text-white data-[state=active]:shadow-md text-[#B58A5E] font-bold text-base py-2.5 transition-all"
                  style={{ fontFamily: '"Nunito Sans", system-ui, sans-serif' }}
                >
                  <UserPlus className="w-5 h-5 mr-2" />
                  Sign Up
                </TabsTrigger>
              </TabsList>
              {/* Login Tab */}
              <TabsContent value="login" className="space-y-3">
                <div className="text-center mb-5">
                  <h2 className="text-xl font-extrabold" style={{ fontFamily: 'var(--dpp-serif)', color: '#22423A', letterSpacing: '1px', fontWeight: 600 }}>Welcome Back!</h2>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="login-email" className="text-gray-700">Email Address</Label>
                    <Input
                      id="login-email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="your@email.com"
                      className="mt-1 border border-gray-300 rounded-xl h-11"
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <Label htmlFor="login-password" className="text-gray-700">Password</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        placeholder="Enter your password"
                        className="mt-1 border border-gray-300 rounded-xl h-11"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-600"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                    {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                  </div>
                  {/* Show general error message if present */}
                  {generalError && (
                    <div className="text-center text-red-600 font-semibold mb-4 bg-red-50 border border-red-200 rounded-xl p-2">
                      {generalError}
                    </div>
                  )}
                  <Button 
                    onClick={handleLogin}
                    disabled={isLoading || !isLoginFormValid}
                    className="w-full h-11 rounded-xl text-base font-bold shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: isLoginFormValid ? 'linear-gradient(90deg, #F7B26A 0%, #ECA7C1 100%)' : '#E5E7EB',
                      color: isLoginFormValid ? 'white' : '#9CA3AF',
                      border: 'none',
                      fontFamily: '"Nunito Sans", system-ui, sans-serif',
                      boxShadow: isLoginFormValid ? '0 4px 12px rgba(247, 178, 106, 0.35)' : 'none',
                    }}
                  >
                    {isLoading ? 'Logging in...' : 'Log in'}
                  </Button>
                  <div className="flex items-center gap-3 my-1">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs text-gray-400 font-semibold">OR</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>
                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={isLoading}
                    className="w-full h-11 rounded-xl bg-white border border-gray-300 flex items-center justify-center gap-2 text-gray-700 font-bold hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontFamily: '"Nunito Sans", system-ui, sans-serif' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                      <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z"/>
                      <path fill="#EA4335" d="M9 3.58c1.32 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                    </svg>
                    Continue with Google
                  </button>

                  <div className="text-center mt-3">
                    <button
                      type="button"
                      onClick={openResetModal}
                      className="text-sm text-[#5E6F66] hover:text-[#22423A] transition-colors duration-200 underline font-bold"
                      style={{ fontFamily: '"Nunito Sans", system-ui, sans-serif' }}
                    >
                      Forgot Password?
                    </button>
                  </div>
                </div>
              </TabsContent>
              {/* Signup Tab */}
              <TabsContent value="signup" className="space-y-4">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-extrabold" style={{ fontFamily: 'var(--dpp-serif)', color: '#22423A', letterSpacing: '1px', fontWeight: 600 }}>Join the Adventure!</h2>
                  <p className="text-gray-600" style={{ fontFamily: '"Nunito Sans", system-ui, sans-serif', color: '#5E6F66' }}>Create your account to start your journey</p>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="signup-name" className="text-gray-700">Your Name</Label>
                    <Input
                      id="signup-name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter your name"
                      className="mt-1 border border-gray-300 rounded-xl h-12"
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <Label htmlFor="signup-email" className="text-gray-700">Email Address</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="your@email.com"
                      className="mt-1 border border-gray-300 rounded-xl h-12"
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <Label htmlFor="signup-password" className="text-gray-700">Password</Label>
                    <div className="relative">
                      <Input
                        id="signup-password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        placeholder="Create a password (6+ characters)"
                        className="mt-1 border border-gray-300 rounded-xl h-12 pr-12"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-600"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                    {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                  </div>
                  {/* Show general error message if present */}
                  {generalError && (
                    <div className="text-center text-red-600 font-semibold mb-4 bg-red-50 border border-red-200 rounded-xl p-2">
                      {generalError}
                    </div>
                  )}
                  <Button 
                    onClick={handleSignup}
                    disabled={isLoading || !isSignupFormValid}
                    className="w-full h-12 rounded-xl text-lg font-bold shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: isSignupFormValid ? 'linear-gradient(90deg, #F7B26A 0%, #ECA7C1 100%)' : '#E5E7EB',
                      color: isSignupFormValid ? 'white' : '#9CA3AF',
                      border: 'none',
                      fontFamily: '"Nunito Sans", system-ui, sans-serif',
                      boxShadow: isSignupFormValid ? '0 4px 12px rgba(247, 178, 106, 0.35)' : 'none',
                    }}
                  >
                    {isLoading ? 'Creating Account...' : 'Get Started 🌟'}
                  </Button>
                  <div className="flex items-center gap-3 my-1">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs text-gray-400 font-semibold">OR</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>
                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={isLoading}
                    className="w-full h-12 rounded-xl bg-white border border-gray-300 flex items-center justify-center gap-2 text-gray-700 font-bold hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontFamily: '"Nunito Sans", system-ui, sans-serif' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                      <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z"/>
                      <path fill="#EA4335" d="M9 3.58c1.32 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                    </svg>
                    Sign up with Google
                  </button>
                </div>
              </TabsContent>
            </Tabs>
            <div className="text-center mt-6 text-sm text-gray-500 flex items-center justify-center gap-2" style={{ fontFamily: '"Nunito Sans", system-ui, sans-serif' }}>
                {/* Rounded pastel SVG lock icon */}
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="5" y="9" width="12" height="8" rx="4" stroke="#22423A" strokeWidth="2" fill="#FCF7EC" />
                  <path d="M11 13v2" stroke="#22423A" strokeWidth="2" strokeLinecap="round" />
                  <path d="M7 9V7a4 4 0 1 1 8 0v2" stroke="#22423A" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span style={{ color: '#22423A' }}>We respect your data and never sell it</span>
            </div>
          </Card>
                <footer style={{ marginTop: 10, color: '#5E6F66', fontSize: 12, textAlign: 'center' }}>© The Dear Parent Project</footer>
          {/* Verification Modal remains unchanged */}
          <Dialog open={showVerificationModal} onOpenChange={setShowVerificationModal}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Email Verification Required</DialogTitle>
                <DialogDescription className="space-y-4 text-gray-700">
                  <p>{verificationStatus || 'Please check your email for a verification link.'}</p>
                  {!auth.currentUser?.emailVerified && (
                    <p className="text-sm text-gray-500">
                      Didn't receive the email? Try signing up again or Login.
                    </p>
                  )}
                </DialogDescription>
              </DialogHeader>
            </DialogContent>
          </Dialog>

          {/* Password Reset Modal */}
          <Dialog open={showResetModal} onOpenChange={setShowResetModal}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-center text-xl font-bold" style={{ fontFamily: 'var(--dpp-serif)', color: '#22423A' }}>
                  Reset Password
                </DialogTitle>
                <DialogDescription className="text-center text-gray-600">
                  Enter your email address and we'll send you a link to reset your password.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="reset-email" className="text-gray-700">Email Address</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="mt-1 border border-gray-300 rounded-xl h-12"
                  />
                </div>
                
                {resetStatus && (
                  <div className={`text-center text-sm p-3 rounded-xl ${
                    resetStatus.includes('sent') || resetStatus.includes('check your inbox') 
                      ? 'text-green-700 bg-green-50 border border-green-200' 
                      : 'text-red-600 bg-red-50 border border-red-200'
                  }`}>
                    {resetStatus}
                  </div>
                )}
              </div>
              
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={closeResetModal}
                  className="flex-1 h-12 rounded-xl border-gray-300"
                  style={{ fontFamily: '"Nunito Sans", system-ui, sans-serif' }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handlePasswordReset}
                  disabled={isResetLoading || !resetEmail.trim()}
                  className="flex-1 h-12 rounded-xl text-white font-bold shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: resetEmail.trim() ? 'linear-gradient(90deg, #F7B26A 0%, #ECA7C1 100%)' : '#E5E7EB',
                    border: 'none',
                    fontFamily: '"Nunito Sans", system-ui, sans-serif',
                    boxShadow: resetEmail.trim() ? '0 4px 12px rgba(247, 178, 106, 0.35)' : 'none',
                  }}
                >
                  {isResetLoading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Sending...</span>
                    </div>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

    </div>

  );
};